import { useCartStore } from '../cart-store';
import { useQueryPromotionData } from '@/hooks/cart.hook';
import { useProductStore } from '../product-store';
import { useState, useEffect } from 'react';
import { NAMESPACE, useLang } from '@/hooks/use-lang';
import { Tooltip, Button } from '@/shared/components';
import dayjs from 'dayjs';
import { Product } from '@/types/cart';
import { useAppStore } from '@/store/app-store';

interface PromotionProps {
  products: Product[];
  init: boolean;
}

export const Promotion = ({ products, init }: PromotionProps) => {
  const { t } = useLang(NAMESPACE.promotion);
  const cartStore = useCartStore();
  const productStore = useProductStore();
  const { shipToList, isAnonymous, billToInfo } = useAppStore();
  const { mutateAsync, isLoading } = useQueryPromotionData();
  const [promoData, setPromoData] = useState(null);
  const defaultCount = 3;
  const [count, setCount] = useState(defaultCount);
  const [showViewMore, setShowViewMore] = useState(false);
  const [showViewLess, setShowViewLess] = useState(false);

  const getFWOData = async () => {
    const billToId = billToInfo?.accountNumber;
    const shipTo = cartStore.address?.accountNumber ?? shipToList?.find((item) => item.isDefault)?.accountNumber;
    const addressKey = cartStore.address?.addressKey;
    const selectedProducts = productStore.products.filter((p) =>
      cartStore.selectedCatalogNumbers.includes(p.catalogNumber)
    );
    const selectedProductsArray = selectedProducts.map((product) => ({
      catalogNumber: product.catalogNumber,
      webCatalogNumber: product.webCatalogNumber,
      quantity: product.quantity,
    }));
    const promoParam = {
      pageType: '3',
      itemList: selectedProductsArray,
    };
    if (!isAnonymous) {
      promoParam['billToId'] = billToId;
      promoParam['shipToId'] = shipTo;
      promoParam['shipToAddressKey'] = addressKey;
    }
    const promo = await mutateAsync(promoParam);
    if (promo) {
      if (promo.data && promo.data.length > 0) {
        setPromoData(promo.data);
        setShowViewLess(false);
        if (promo.data.length > defaultCount) {
          setShowViewMore(true);
          setCount(defaultCount);
        } else {
          setShowViewMore(false);
          setCount(promo.data.length);
        }
      } else {
        setPromoData([]);
      }
    }
  };

  useEffect(() => {
    if (isLoading) return;
    if (!products) return;
    if (init && products?.length !== 0) return;
    if (!isAnonymous && !cartStore.address) return;
    getFWOData();
  }, [products, cartStore.selectedCatalogNumbers, init, cartStore.address]);
  if (promoData?.length === 0) {
    return null;
  }

  const copy = (index) => {
    navigator.clipboard.writeText(promoData[index].promoCode);
  };
  return (
    <>
      {promoData && (
        <div className="mt-4 border border-solid border-gray-450 rounded pb-3">
          <h1 className="font-medium px-3 pt-2">{t('promotion.code.title')}</h1>
          {promoData?.slice(0, count).map((item, index) => {
            return (
              <div className="flex flex-row justify-between items-center pt-4 min-h-[90px] px-2.5" key={index}>
                <div className="wrap_contain w-[calc(100%-120px)]">
                  <div className="h-[92px] flex flex-col justify-around items-start rounded-lg border-2 border-dashed border-[#A9A9A9] bg-[#F5F5F5] px-5 py-2">
                    <div className="flex gap-4">
                      <div className="">
                        <div className="font-bold text-lg whitespace-nowrap overflow-hidden text-ellipsis">
                          {item.promoName}
                        </div>
                        <Tooltip title={item.promoDesc} htmlTitle={item.promoDesc} placement="top">
                          <div
                            className="desc-parent text-ellipsis overflow-hidden whitespace-nowrap h-[25px] max-w-[165px]"
                            dangerouslySetInnerHTML={{ __html: item.promoDesc }}
                          ></div>
                        </Tooltip>
                        {item?.promoTo && (
                          <div className="text-base text-[#A9A9A9]">
                            {t('valid.until')}&nbsp;{dayjs(item?.promoTo).format('DD-MM-YY')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="wrap_right_top_grey"></div>
                  <div className="wrap_right_bottom_grey"></div>
                </div>
                <div className="wrap_contain w-[120px]">
                  <div className="text-center min-h-[92px] rounded-lg border-y-2 border-r-2 border-l-0 border-dashed border-[#A9A9A9] bg-[#F5F5F5] flex flex-col justify-center">
                    <div className="font-bold text-base">{item.promoCode}</div>
                    <div
                      className="text-[#1e8ae7] underline cursor-pointer"
                      onClick={() => {
                        copy(index);
                      }}
                    >
                      {t('copy')}
                    </div>
                  </div>
                  <div className="wrap_left_top_grey"></div>
                  <div className="wrap_left_bottom_grey"></div>
                </div>
              </div>
            );
          })}
          {showViewMore && (
            <div className="flex justify-center items-center pt-2">
              <Button
                type="link"
                onClick={() => {
                  setCount(promoData.length);
                  setShowViewMore(false);
                  setShowViewLess(true);
                }}
              >
                {t('view.more')}
              </Button>
            </div>
          )}
          {showViewLess && (
            <div className="flex justify-center items-center pt-2">
              <Button
                type="link"
                onClick={() => {
                  setCount(defaultCount);
                  setShowViewMore(true);
                  setShowViewLess(false);
                }}
              >
                {t('view.less')}
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
};
